(() => {
  'use strict';

  if (window.WUBProductController) return;

  const stateScripts = new Map();
  const registry = new Map();
  const ROOT_SELECTOR = '[data-wub-product-root], .wub-main-product-v2[data-section-id][data-product-id]';
  let stickyFrame = 0;

  const escapeCss = (value) => window.CSS?.escape
    ? window.CSS.escape(String(value))
    : String(value).replace(/["\\]/g, '\\$&');

  const parseJson = (script) => {
    if (!script) return null;
    try { return JSON.parse(script.textContent || '{}'); }
    catch (error) { console.warn('WUB product state parse failed', error); return null; }
  };

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  function updateVariantUrl(variantId) {
    if (!window.history?.replaceState || typeof window.URL !== 'function') return;
    const nextVariantId = String(variantId || '');
    if (!nextVariantId) return;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === nextVariantId) return;
      url.searchParams.set('variant', nextVariantId);
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (error) {
      console.warn('WUB variant URL update failed', error);
    }
  }

  function canonicalRoot(root) {
    if (!root) return null;
    if (!root.hasAttribute('data-wub-product-root')) root.setAttribute('data-wub-product-root', '');
    return root;
  }

  function rootForSection(sectionId) {
    if (!sectionId) return null;
    const escaped = escapeCss(sectionId);
    return canonicalRoot(document.querySelector(
      `[data-wub-product-root][data-section-id="${escaped}"], .wub-main-product-v2[data-section-id="${escaped}"][data-product-id]`
    ));
  }

  function stickyForSection(sectionId) {
    if (!sectionId) return null;
    return document.querySelector(
      `product-add-to-cart-sticky.product-add-to-cart-sticky--wub[data-section="${escapeCss(sectionId)}"]`
    );
  }

  function hydrateState(sectionId) {
    const key = String(sectionId || '');
    if (!key) return null;
    const script = document.querySelector(`[data-wub-product-state-data][id="WubProductStateData-${escapeCss(key)}"]`);
    if (!script) return null;
    if (stateScripts.get(key) === script && registry.has(key)) return registry.get(key);

    const payload = parseJson(script);
    if (!payload?.variants?.length) return null;

    const variants = new Map(payload.variants.map((variant) => [String(variant.id), variant]));
    const sectionState = {
      sectionId: key,
      productId: payload.productId,
      productUrl: payload.productUrl,
      variants,
      selectedVariantId: String(payload.initialVariantId || payload.variants[0].id),
      quantity: 1
    };

    const initial = variants.get(sectionState.selectedVariantId);
    sectionState.quantity = Math.max(Number(initial?.quantityMin || 1), 1);
    registry.set(key, sectionState);
    stateScripts.set(key, script);
    return sectionState;
  }

  function current(sectionId) {
    const state = hydrateState(String(sectionId || ''));
    if (!state) return null;
    return { state, variant: state.variants.get(String(state.selectedVariantId)) || null };
  }

  function resolveSectionId(node) {
    const productRoot = node?.closest?.(ROOT_SELECTOR);
    if (productRoot?.dataset?.sectionId) return productRoot.dataset.sectionId;

    const atcRoot = node?.closest?.('[data-wub-product-atc][data-section-id]');
    if (atcRoot?.dataset?.sectionId) return atcRoot.dataset.sectionId;

    const stickyRoot = node?.closest?.('product-add-to-cart-sticky[data-section]');
    if (stickyRoot?.dataset?.section) return stickyRoot.dataset.section;

    return node?.dataset?.sectionId || node?.dataset?.section || '';
  }

  function quantityBounds(variant) {
    const min = Math.max(1, Number(variant?.quantityMin || 1));
    const increment = Math.max(1, Number(variant?.quantityIncrement || 1));
    const rawMax = Number(variant?.quantityMax);
    const max = Number.isFinite(rawMax) && rawMax > 0 ? rawMax : null;
    return { min, max, increment };
  }

  function normalizeQuantity(variant, value) {
    const { min, max, increment } = quantityBounds(variant);
    let quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity)) quantity = min;
    quantity = Math.max(min, quantity);
    quantity = min + Math.round((quantity - min) / increment) * increment;
    if (max !== null) quantity = Math.min(max, quantity);
    return Math.max(min, quantity);
  }

  function stockState(variant, quantity, cfg = {}) {
    const inventory = Number(variant?.inventoryQuantity || 0);
    const managed = Boolean(variant?.inventoryManagement);
    const canBackorder = variant?.inventoryPolicy === 'continue';
    const requested = Math.max(1, Number(quantity || 1));

    if (!variant?.available) {
      return { status: 'out_of_stock', text: cfg.outOfStockText || '', tone: 'out_of_stock' };
    }

    if (!managed) {
      return { status: 'in_stock', text: cfg.inStockText || '', tone: 'in_stock' };
    }

    if (inventory >= requested) {
      return { status: 'in_stock', text: cfg.inStockText || '', tone: 'in_stock' };
    }

    if (canBackorder) {
      if (inventory > 0 && cfg.showSplitMessage) {
        const delayed = Math.max(0, requested - inventory);
        const text = String(cfg.splitTextTemplate || '')
          .replace('[available]', String(inventory))
          .replace('[backorder]', String(delayed));
        return { status: 'backorder', text, tone: 'backorder' };
      }
      return { status: 'backorder', text: cfg.backorderText || '', tone: 'backorder' };
    }

    return { status: 'out_of_stock', text: cfg.outOfStockText || '', tone: 'out_of_stock' };
  }

  function quantityScopes(root, sectionId) {
    return [root, stickyForSection(sectionId)].filter(Boolean);
  }

  function syncQuantityMarkup(root, sectionId, variant, quantity) {
    if (!root || !variant) return;
    const { min, max, increment } = quantityBounds(variant);

    quantityScopes(root, sectionId).forEach((scope) => {
      scope.querySelectorAll('[data-wub-quantity-input], [data-wub-sticky-quantity-input]').forEach((input) => {
        input.min = String(min);
        input.step = String(increment);
        if (max === null) input.removeAttribute('max');
        else input.max = String(max);
        input.value = String(quantity);
      });

      scope.querySelectorAll('[data-wub-quantity-minus]').forEach((button) => {
        button.disabled = quantity <= min;
      });
      scope.querySelectorAll('[data-wub-quantity-plus]').forEach((button) => {
        button.disabled = max !== null && quantity >= max;
      });
    });
  }

  function updatePrice(root, sectionId, variant) {
    if (!root || !variant) return;
    [root, stickyForSection(sectionId)].filter(Boolean).forEach((scope) => {
      scope.querySelectorAll('[data-wub-psr-current-price], [data-wub-sticky-current-price]').forEach((node) => {
        node.textContent = variant.priceFormatted || '';
      });
      scope.querySelectorAll('[data-wub-psr-compare-price], [data-wub-sticky-compare-price]').forEach((node) => {
        const hasCompare = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
        node.textContent = hasCompare ? (variant.compareAtPriceFormatted || '') : '';
        node.classList.toggle('is-hidden', !hasCompare);
      });
    });
  }

  function updateStock(root, variant, quantity) {
    const row = root?.querySelector('[data-wub-price-stock-row]');
    const host = row?.querySelector('[data-wub-psr-stock]');
    const text = host?.querySelector('[data-wub-psr-stock-text]');
    if (!row || !host || !text || !variant) return;

    const result = stockState(variant, quantity, {
      inStockText: row.dataset.inStockText || '',
      backorderText: row.dataset.backorderText || '',
      outOfStockText: row.dataset.outOfStockText || '',
      splitTextTemplate: row.dataset.splitTextTemplate || '',
      showSplitMessage: row.dataset.showSplitMessage === 'true'
    });

    host.dataset.status = result.status;
    text.textContent = result.text;
    const colorPrefix = result.status === 'in_stock' ? 'inStock' : result.status === 'backorder' ? 'backorder' : 'outOfStock';
    host.style.setProperty('--wub-psr-stock-text-color', row.dataset[`${colorPrefix}TextColor`] || '');
    host.style.setProperty('--wub-psr-stock-icon-color', row.dataset[`${colorPrefix}IconColor`] || '');
  }

  function updateMainButton(root, variant) {
    const button = root?.querySelector('[data-wub-main-atc-button]');
    if (!button || !variant) return;
    const label = button.querySelector('[data-wub-main-atc-text]');
    const available = Boolean(variant.available);
    button.disabled = !available;
    button.classList.toggle('sold-out', !available);
    if (label) label.textContent = available ? (button.dataset.labelAvailable || '') : (button.dataset.labelSoldOut || button.dataset.labelUnavailable || '');
  }

  function updateSticky(sectionId, variant) {
    const sticky = stickyForSection(sectionId);
    if (!sticky || !variant) return;

    sticky.querySelectorAll('[data-wub-sticky-variant-input]').forEach((input) => {
      input.value = String(variant.id);
    });
    sticky.querySelectorAll('[data-wub-sticky-variant-select]').forEach((select) => {
      if (select.value !== String(variant.id)) select.value = String(variant.id);
    });
    sticky.querySelectorAll('[data-wub-sticky-atc-button]').forEach((button) => {
      const available = Boolean(variant.available);
      button.disabled = !available;
      button.classList.toggle('is-disabled', !available);
      const text = button.querySelector('[data-wub-sticky-atc-text]');
      if (text) text.textContent = available ? (button.dataset.labelAvailable || '') : (button.dataset.labelSoldOut || '');
    });
  }

  function updateVariantInputs(root, variant) {
    if (!root || !variant) return;
    root.querySelectorAll('[data-wub-variant-input]').forEach((input) => {
      input.value = String(variant.id);
    });
  }

  function optionGroupIndex(group) {
    const explicit = Number(group?.dataset?.wubOptionIndex);
    if (Number.isFinite(explicit)) return explicit;
    const legacy = String(group?.dataset?.index || '').match(/^option(\d+)$/i);
    if (legacy) return Math.max(0, Number(legacy[1]) - 1);
    return Number.MAX_SAFE_INTEGER;
  }

  function optionGroups(root) {
    if (!root) return [];
    const candidates = [
      ...root.querySelectorAll('[data-wub-option-index]'),
      ...root.querySelectorAll('.wub-pdp-v2__variant-picker [data-index^="option"]')
    ];
    return [...new Set(candidates)].sort((a, b) => optionGroupIndex(a) - optionGroupIndex(b));
  }

  function syncOptionMarkup(root, variant) {
    if (!root || !variant) return;
    optionGroups(root).forEach((group, index) => {
      const value = variant.options?.[index];
      if (value == null) return;
      const expected = String(value);

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = String(input.value) === expected;
      });

      const select = group.querySelector('select');
      if (select && [...select.options].some((option) => String(option.value) === expected)) {
        select.value = expected;
      }

      const selectedLabel = group.querySelector('.form__label__value');
      if (selectedLabel) selectedLabel.textContent = expected;
    });
  }

  function selectedOptions(root) {
    return optionGroups(root).map((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
      const select = group.querySelector('select');
      return select?.value || '';
    });
  }

  function variantFromOptions(state, selected) {
    const values = Array.isArray(selected) ? selected : [];
    for (const variant of state.variants.values()) {
      if (variant.options?.every((value, index) => String(value) === String(values[index] ?? ''))) return variant;
    }
    return null;
  }

  function updateStickyVisibilityForSection(sectionId) {
    const root = rootForSection(sectionId);
    const sticky = stickyForSection(sectionId);
    if (!sticky) return;

    const anchor = root?.querySelector('[data-wub-main-atc-button]') || root?.querySelector('[data-wub-product-atc]');
    const shouldShow = Boolean(anchor && anchor.getBoundingClientRect().bottom <= 0);
    sticky.classList.toggle('is-visible', shouldShow);
    sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  function updateAllStickyVisibility() {
    rootsIn(document).forEach((root) => {
      const sectionId = root.dataset.sectionId;
      if (sectionId) updateStickyVisibilityForSection(sectionId);
    });
  }

  function scheduleStickyVisibility() {
    if (stickyFrame) return;
    stickyFrame = window.requestAnimationFrame(() => {
      stickyFrame = 0;
      updateAllStickyVisibility();
    });
  }

  function stateDetail(state, variant, reason) {
    return {
      sectionId: String(state.sectionId),
      productId: state.productId,
      variantId: Number(variant.id),
      variant,
      quantity: state.quantity,
      available: Boolean(variant.available),
      reason
    };
  }

  function sync(sectionId, reason = 'sync') {
    const currentState = current(sectionId);
    if (!currentState?.variant) return;
    const { state, variant } = currentState;
    const root = rootForSection(sectionId);
    if (!root) return;

    state.quantity = normalizeQuantity(variant, state.quantity);
    syncOptionMarkup(root, variant);
    updateVariantInputs(root, variant);
    syncQuantityMarkup(root, sectionId, variant, state.quantity);
    updatePrice(root, sectionId, variant);
    updateStock(root, variant, state.quantity);
    updateMainButton(root, variant);
    updateSticky(sectionId, variant);
    scheduleStickyVisibility();

    const detail = stateDetail(state, variant, reason);
    emit('wub:product-state-change', detail);
    emit('wub:product-state', detail);
  }

  function selectVariant(sectionId, variantId, reason = 'variant') {
    const state = hydrateState(String(sectionId || ''));
    if (!state || !state.variants.has(String(variantId))) return false;
    state.selectedVariantId = String(variantId);
    const variant = state.variants.get(state.selectedVariantId);
    state.quantity = normalizeQuantity(variant, state.quantity);
    sync(String(sectionId), reason);
    updateVariantUrl(variant.id);
    return true;
  }

  function setQuantity(sectionId, quantity, reason = 'quantity') {
    const currentState = current(String(sectionId || ''));
    if (!currentState?.variant) return false;
    currentState.state.quantity = normalizeQuantity(currentState.variant, quantity);
    sync(String(sectionId), reason);
    return true;
  }

  function selectFromOptions(root, reason = 'option') {
    const sectionId = resolveSectionId(root);
    const state = hydrateState(String(sectionId || ''));
    if (!state) return false;
    const variant = variantFromOptions(state, selectedOptions(root));
    return variant ? selectVariant(sectionId, variant.id, reason) : false;
  }

  function syncMainFormBeforeSubmit(form) {
    const atc = form?.closest?.('[data-wub-product-atc][data-section-id]');
    if (!atc) return;
    const sectionId = atc.dataset.sectionId;
    const root = rootForSection(sectionId);
    const state = hydrateState(sectionId);
    if (!root || !state) return;

    const rawQuantity = atc.querySelector('[data-wub-quantity-input]')?.value ?? state.quantity;
    const variant = variantFromOptions(state, selectedOptions(root)) || state.variants.get(String(state.selectedVariantId));
    if (!variant) return;

    state.selectedVariantId = String(variant.id);
    state.quantity = normalizeQuantity(variant, rawQuantity);
    sync(sectionId, 'pre-submit');
  }

  document.addEventListener('change', (event) => {
    const stickySelect = event.target.closest?.('[data-wub-sticky-variant-select]');
    if (stickySelect) {
      const sectionId = resolveSectionId(stickySelect);
      if (sectionId) selectVariant(sectionId, stickySelect.value, 'sticky-select');
      return;
    }

    const optionGroup = event.target.closest?.('[data-wub-option-index], .wub-pdp-v2__variant-picker [data-index^="option"]');
    if (optionGroup && event.target.matches?.('input, select')) {
      const root = canonicalRoot(optionGroup.closest(ROOT_SELECTOR));
      selectFromOptions(root, 'option-change');
      return;
    }

    const quantity = event.target.closest?.('[data-wub-quantity-input]');
    if (quantity) {
      const sectionId = resolveSectionId(quantity);
      if (sectionId) setQuantity(sectionId, quantity.value, 'quantity-input');
    }
  });

  document.addEventListener('click', (event) => {
    const minus = event.target.closest?.('[data-wub-quantity-minus]');
    const plus = event.target.closest?.('[data-wub-quantity-plus]');
    if (!minus && !plus) return;
    const control = minus || plus;
    const sectionId = resolveSectionId(control);
    const currentState = current(sectionId);
    if (!currentState?.variant) return;
    const { increment } = quantityBounds(currentState.variant);
    const next = currentState.state.quantity + (plus ? increment : -increment);
    setQuantity(sectionId, next, plus ? 'quantity-plus' : 'quantity-minus');
  });

  document.addEventListener('submit', (event) => {
    syncMainFormBeforeSubmit(event.target);
  }, true);

  function mount(root) {
    const canonical = canonicalRoot(root);
    const sectionId = canonical?.dataset?.sectionId;
    if (!sectionId || !hydrateState(sectionId)) return;
    sync(sectionId, 'mount');
    updateStickyVisibilityForSection(sectionId);
  }

  function rootsIn(scope = document) {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return [...new Set(roots)].map(canonicalRoot).filter(Boolean);
  }

  function mountAll(scope = document) {
    rootsIn(scope).forEach(mount);
  }

  document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  document.addEventListener('shopify:section:load', (event) => {
    mountAll(event.target);
    scheduleStickyVisibility();
  });
  window.addEventListener('scroll', scheduleStickyVisibility, { passive: true });
  window.addEventListener('resize', scheduleStickyVisibility);
  window.addEventListener('pageshow', scheduleStickyVisibility);
  if (document.readyState !== 'loading') mountAll();

  window.WUBProductController = Object.freeze({
    mountAll,
    selectVariant,
    setQuantity,
    sync,
    getState: (sectionId) => current(String(sectionId || '')),
    stockState
  });
})();
